import { Canvas } from 'fabric';

/**
 * Интерфейс для состояния блокировки канваса
 */
interface CanvasLockState {
  isLocked: boolean;
  lockReason?: string;
  lockTimestamp: number;
  lockCount: number; // Счетчик вложенных блокировок
}

/**
 * Расширение типа Canvas для добавления поля блокировки
 */
declare module 'fabric' {
  interface Canvas {
    _lockState?: CanvasLockState;
  }
}

/**
 * Блокирует канвас от интерактивности
 * @param canvas - Инстанс канваса
 * @param reason - Причина блокировки (опционально)
 * @returns Функция для разблокировки
 */
export function lockCanvas(canvas: Canvas, reason?: string): () => void {
  if (!canvas) {
    console.warn('🚫 Попытка заблокировать несуществующий канвас');
    return () => {};
  }

  // Инициализируем состояние блокировки если его нет
  if (!canvas._lockState) {
    canvas._lockState = {
      isLocked: false,
      lockCount: 0,
      lockTimestamp: 0,
    };
  }

  const lockState = canvas._lockState;

  // Увеличиваем счетчик блокировок
  lockState.lockCount++;
  lockState.isLocked = true;
  lockState.lockReason = reason;
  lockState.lockTimestamp = Date.now();

  console.debug(
    `🔒 Канвас заблокирован (${lockState.lockCount}):`,
    reason || 'без указания причины',
  );

  // Отключаем интерактивность канваса
  canvas.selection = false;
  canvas.defaultCursor = 'wait';

  // Блокируем все объекты на канвасе
  const objects = canvas.getObjects();
  objects.forEach((obj) => {
    obj.selectable = false;
    obj.evented = false;
  });

  // Возвращаем функцию для разблокировки
  return () => unlockCanvas(canvas);
}

/**
 * Разблокирует канвас
 * @param canvas - Инстанс канваса
 */
export function unlockCanvas(canvas: Canvas): void {
  if (!canvas || !canvas._lockState) {
    console.warn('🚫 Попытка разблокировать несуществующий или незаблокированный канвас');
    return;
  }

  const lockState = canvas._lockState;

  // Уменьшаем счетчик блокировок
  lockState.lockCount = Math.max(0, lockState.lockCount - 1);

  // Если счетчик достиг нуля, полностью разблокируем
  if (lockState.lockCount === 0) {
    lockState.isLocked = false;
    lockState.lockReason = undefined;

    console.debug('🔓 Канвас разблокирован');

    // Восстанавливаем интерактивность канваса
    canvas.selection = true;
    canvas.defaultCursor = 'default';

    // Разблокируем все объекты на канвасе
    const objects = canvas.getObjects();
    objects.forEach((obj) => {
      obj.selectable = true;
      obj.evented = true;
    });
  } else {
    console.debug(`🔓 Частичная разблокировка канваса (осталось: ${lockState.lockCount})`);
  }
}

/**
 * Проверяет, заблокирован ли канвас
 * @param canvas - Инстанс канваса
 * @returns true если канвас заблокирован
 */
export function isCanvasLocked(canvas: Canvas): boolean {
  return !!canvas?._lockState?.isLocked;
}

/**
 * Получает информацию о блокировке канваса
 * @param canvas - Инстанс канваса
 * @returns Информация о блокировке или null
 */
export function getCanvasLockInfo(canvas: Canvas): CanvasLockState | null {
  return canvas?._lockState || null;
}

/**
 * Принудительно разблокирует канвас (сбрасывает счетчик)
 * Используется только в экстренных случаях
 * @param canvas - Инстанс канваса
 */
export function forceUnlockCanvas(canvas: Canvas): void {
  if (!canvas) return;

  if (canvas._lockState) {
    console.warn('⚠️ Принудительная разблокировка канваса');
    canvas._lockState.lockCount = 0;
    unlockCanvas(canvas);
  }
}
